import React from 'react';
import styled from 'styled-components';
import { Typography } from '@vp/react-vp-ui-library';
import { useMappedState } from 'redux-react-hook';
import { getLocalizedText as t } from '@vp-dst/localization-util';

import Slide from './Slide';
import { createProject } from '../services/project';
import { LoadablePrice } from './Price';
import { ProductSpecs } from '../services/specs';
import { getLocaleDefaultSize } from '../utilities/size';
import ProductIcon from './ProductIcon';
import { Product as ProductType } from '../services/products';
import { State } from '../state/reducer';
import websiteContext from '../websiteContext';
import { logAction, logError, logEvent } from '../utilities/logger';

const UploadLabel = styled(Typography)`
  margin: 0;
`;

interface ProductSlideProps {
  product: ProductType;
}

function determineScaleFactor(
  productSpecsBySKU: { [sku: string]: ProductSpecs },
  maxHeight: number,
  maxWidth: number,
) {
  const maxProductHeight = Math.max(
    ...Object.values(productSpecsBySKU).map(specs => specs.bleedCentimeters.height),
  );
  const maxProductWidth = Math.max(
    ...Object.values(productSpecsBySKU).map(specs => specs.bleedCentimeters.width),
  );

  const widthScale = maxWidth / maxProductWidth;
  const heightScale = maxHeight / maxProductHeight;

  return Math.min(widthScale, heightScale);
}

const mapState = (state: State) => {
  return {
    productSpecsBySKU: state.loadableProductSpecsBySKU!.data as { [sku: string]: ProductSpecs },
  };
};

export default function ProductSlide(props: ProductSlideProps) {
  const { product } = props;

  const { productSpecsBySKU } = useMappedState(mapState);

  const createProjectAndGoToStudio = async () => {
    try {
      const project = await createProject(
        product.mpId,
        product.sku,
        product.baseQuantity,
        product.attributeChoices,
      );

      logEvent('ProjectCreated', { projectId: project.id });

      window.location.href = `/studio.aspx?project_id=${project.id}&xnav=UploadDesign:${
        product.sku
      }`;
    } catch (error) {
      // safari 12 (on iOS and OSX) has a bug where it fires this error when we try to use the Beacon api to track page-leave events
      // newrelic attempts to fire such an event, so we get this error every time someone leaves our page (or "hides" it on mobile)
      // per newrelic's suggestion, we'll ignore the error for now, and hope Safari fixes the bug
      // ref: https://discuss.newrelic.com/t/beacon-api-cannot-load-due-to-access-control-checks/68332
      if (
        error &&
        typeof error.message === 'string' &&
        error.message.startsWith('Beacon API cannot load')
      ) {
        console.log('ignored', error);
        return;
      }

      logError(error);

      // todo: show error in UI?
    }
  };

  const maxHeight = 200;
  const maxWidth = 200;
  const scaleFactor = determineScaleFactor(productSpecsBySKU, maxHeight, maxWidth);

  return (
    <Slide
      image={
        <ProductIcon
          maxWidth={maxWidth}
          maxHeight={maxHeight}
          specs={product.specs}
          scaleFactor={scaleFactor}
          onClick={() => {
            logAction('ProductCarousel', 'SlideImage', 'Clicked', { sku: product.sku });
            createProjectAndGoToStudio();
          }}
        />
      }
      title={product.shortName}
      subTitle={`(${getLocaleDefaultSize(
        product.specs.trimCentimeters.width,
        product.specs.trimCentimeters.height,
        websiteContext.getLanguage(),
      )})`}
      price={<LoadablePrice price={{ __html: product.upsellPriceHTML }} />}
      action={
        <UploadLabel
          onClick={() => {
            logAction('ProductCarousel', 'SelectButton', 'Clicked', { sku: product.sku });
            createProjectAndGoToStudio();
          }}
          strong={true}
        >
          {t('select')}
        </UploadLabel>
      }
    />
  );
}
